<template>
    <div v-dark class="study-progress">
        <ResetProgressModal
            v-if="showResetProgressModal"
            :is-loading="isLoading"
            @resetProgress="resetProgress"
            @close="showResetProgressModal = false"
        />
        <div v-dark class="study-progress__head">
            <div v-dark class="study-progress__title">
                <Icon type="questions" />
                <h2>Study Progress</h2>
            </div>
            <PocketLink
                v-if="bestAnswers.length"
                :is-dark-mode="isDarkMode"
                @click="showResetProgressModal = true"
            >
                Reset Progress
            </PocketLink>
        </div>
        <div class="study-progress__progress-container">
            <div v-dark class="study-progress__progress-left">
                {{ bestAnswers.length }}<span>/</span>{{ totalQuestionCount }}
            </div>
            <div class="study-progress__progress-right">
                <div v-dark class="study-progress__progress-percent">
                    {{ Math.round(((bestAnswers.length / totalQuestionCount) || 0) * 100) }}<span>%</span>
                </div>
                <div v-dark class="study-progress__progress-bar">
                    <div
                        v-dark
                        class="study-progress__progress-bar-fill"
                        :class="{ 'study-progress__progress-bar-fill--100': bestAnswers.length === totalQuestionCount }"
                        :style="{ width: `${Math.round((bestAnswers.length / totalQuestionCount) * 100)}%`}"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'
import { subscriptionModule } from '@/store/subscription/module'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'
import ResetProgressModal from '@/components/Stats/ResetProgressModal.vue'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'

const {
    Icon,
    Link: PocketLink,
} = UIKit

const showResetProgressModal = ref(false)
const isLoading = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const hasSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const bestAnswers = computed(() => {
    return Object.values(
        quizModule.getters.getLatestAnswers({ questionFilter: hasSubscription.value ? 'all' : 'free' })
    )
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const totalQuestionCount = computed(() => {
    if (hasSubscription.value) {
        return ((examMetadata.value?.itemCount || 0) - (examMetadata.value?.archivedCount || 0))
    }

    return examMetadata.value?.specialQuestions || 0
})

onMounted(() => {
    examMetadataModule.actions.fetchExamMetadata()
    userModule.actions.fetchUserData()
})

const resetProgress = async () => {
    if (!isLoading.value && examMetadata.value) {
        isLoading.value = true
        await userExamMetadataModule.actions.resetExamProgress(examMetadata.value)
        showResetProgressModal.value = false
        isLoading.value = false
    }
}
</script>

<style lang="scss" scoped>
.study-progress {
    background: $white;
    border-radius: 6px;
    padding: 3px 16px 12px;
    box-sizing: border-box;
    height: 95px;
    position: relative;

    @include breakpoint(black-bear) {
        padding: 4px 10px 0;
        height: 93px;
    }

    &--dark {
        background: rgba($white, 0.02);
        color: $fog;

        @include breakpoint(black-bear) {
            background: $brand-black;
        }
    }

    &__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 38px;
        border-bottom: 1px solid $gray-divider;
        padding-left: 8px;

        &--dark {
            border-color: $pewter;
        }
    }

    &__title {
        display: flex;
        align-items: center;

        svg {
            height: 22px;
            margin-right: 8px;
            color: $slate;
        }

        &--dark svg {
            color: $pewter;
        }

        h2 {
            margin: 0;
            font-size: 16px;
            line-height: 22px;
            font-weight: 600;
        }
    }

    &__progress-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 19px;
        margin-left: 2px;
        padding: 0 9px;
        box-sizing: border-box;

        @include breakpoint(black-bear) {
            margin-top: 18px;
        }
    }

    &__progress-left {
        font-size: 16px;
        letter-spacing: -0.3px;
        line-height: 15px;
        font-weight: 400;
        color: $slate-03;
        width: 100px;

        &--dark {
            color: $white;
        }

        span {
            font-size: 16px;
            line-height: 15px;
            letter-spacing: 0.43px;
            margin: 0 2px;
            color: $pewter;
        }
    }

    &__progress-right {
        display: flex;
        align-items: center;
        flex: 1;
    }

    &__progress-percent {
        font-weight: 600;
        letter-spacing: -0.3px;
        line-height: 15px;
        font-size: 18px;
        margin-right: 10px;

        &--dark {
            color: $white;
        }

        span {
            letter-spacing: 0.48px;
            font-weight: 400;
            margin-left: 2px;
        }
    }

    &__progress-bar {
        width: calc(100% - 2px);
        border-radius: 2px;
        background: $pearl;
        height: 12px;
        position: relative;
        border: 1px solid rgba($pewter, 0.85);

        &--dark {
            background: $moonlit-ocean;
            border-color: $slate;
        }
    }

    &__progress-bar-fill {
        background: $brand-black;
        height: 14px;
        position: absolute;
        top: -1px;
        left: -1px;
        border-radius: 2px;

        &--dark {
            background: $pewter;
        }

        &--100 {
            background: url('@/assets/stats/progress-easter-egg.svg') no-repeat center center;
            background-size: cover;
            width: calc(100% + 2px) !important;
        }
    }
}
</style>
